// The "what you just saved, and where to go next" banner a capture board shows
// after its form closes (ui-flow F01 UF-06). Same look as the berry board's
// receive banner, which still renders its own (berry hand-off is UF-12).
//
// It is NOT save feedback — <SubmitOverlay/> still owns pending / tick / error.
// The tick is gone in a second; this stays until the operator dismisses it or
// starts the next capture, so the truck they just recorded is one tap away.

import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { CheckCircle2, X } from 'lucide-react'
import clsx from 'clsx'
import { statusToneClasses } from '@/lib/statusTone'

const GOOD = statusToneClasses('good')

export interface SavedBannerProps {
  /** Bold first line: what happened ("Run R-12 completed"). */
  title?: ReactNode
  /** A reference (run, mission, delivery number), shown in tabular mono. */
  reference?: ReactNode
  /** What was saved — a sentence, or lines. */
  children?: ReactNode
  /** The next steps (usually <SavedBannerLink/>s). */
  actions?: ReactNode
  /** Shows a 44px dismiss button when given. */
  onDismiss?: () => void
  className?: string
}

export default function SavedBanner({ title, reference, children, actions, onDismiss, className }: SavedBannerProps) {
  return (
    <div role="status"
      className={clsx('rounded-xl border border-line px-4 py-3 text-sm print:hidden', GOOD.pill, className)}>
      <div className="flex items-start gap-2">
        <CheckCircle2 size={16} className="shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1 min-w-0">
          {(title || reference) && (
            <p className="font-semibold">
              {title}
              {reference && <span className="ml-2 font-mono tabular-nums">{reference}</span>}
            </p>
          )}
          {children}
        </div>
        {onDismiss && (
          <button type="button" aria-label="Dismiss saved message" onClick={onDismiss}
            className="-my-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-tone-good-bg shrink-0">
            <X size={16} />
          </button>
        )}
      </div>
      {actions && <div className="mt-2 flex flex-wrap gap-2 pl-6">{actions}</div>}
    </div>
  )
}

const NEXT_STEP = 'min-h-[44px] inline-flex items-center gap-1 rounded-lg border border-line px-3 font-semibold hover:bg-tone-good-bg'

/** A next-step link inside the banner — a 44px glove-sized target. */
export function SavedBannerLink({ className, ...props }: LinkProps) {
  return <Link {...props} className={clsx(NEXT_STEP, className)} />
}

/** A next step that is an action (print, email), not a route. Same 44px look. */
export function SavedBannerButton({ className, type = 'button', ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} type={type} className={clsx(NEXT_STEP, className)} />
}
